import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip } from '@swyftx/aviary/atoms/Chip';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Heading, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useLoanHealthGauge } from '@swyftx/features/loans/components/LoanHealth/api/hooks/useLoanHealthGauge';
import { HealthStatus } from '@swyftx/features/loans/components/LoanHealth/api/types/loanHealth.types';

import { cn } from '@shared/utils/lib/ui';

import GaugeSvg from './GaugeSvg';

type LoanHealthGaugeProps = {
  id: string;
  size?: 'lg' | 'sm';
  className?: string;
  healthPercentage: number;
  backgroundFillColor?: string;
};

const LoanHealthGauge: React.FC<LoanHealthGaugeProps> = ({
  id,
  size = 'sm',
  className,
  healthPercentage,
  backgroundFillColor,
}) => {
  const {
    healthStatus,
    statusTitle,
    pathFillColor,
    textColor,
    chipColor,
    chipText,
    adjustedHealthPercentage,
    calculateChartData,
  } = useLoanHealthGauge(healthPercentage);
  const height = size === 'lg' ? 350 : 300;
  const innerRadius = height * 0.4;
  const { t } = useTranslation('loans');

  const chartData = useMemo(
    () => calculateChartData(adjustedHealthPercentage, pathFillColor),
    [adjustedHealthPercentage, pathFillColor],
  );

  return (
    <FlexLayout className={cn('mb-0 h-full w-full', className)} alignItems='center' justifyContent='center'>
      <div className='relative'>
        <GaugeSvg
          id={id}
          size={height}
          innerRadius={innerRadius}
          chartData={chartData}
          adjustedHealthPercentage={adjustedHealthPercentage}
          pathFillColor={pathFillColor}
          backgroundFillColor={backgroundFillColor}
        />
        <div
          className={`absolute left-1/2 ${
            healthStatus === HealthStatus.Inactive ? 'top-1/3' : 'top-1/4'
          } -translate-x-1/2 p-16`}
        >
          <FlexLayout direction='column' alignItems='center' spacing={size === 'lg' ? 8 : 4}>
            <Numeric size={size == 'lg' ? 'hero' : 'xxlarge'}>
              {healthStatus === HealthStatus.Inactive ? '--' : `${healthPercentage}%`}
            </Numeric>
            <Heading size={size == 'lg' ? 'h4' : 'h5'} color={textColor} className='text-center'>
              {statusTitle}
            </Heading>
            {chipText && (
              <Chip variant='subtle' color={chipColor} size='md' className='outline-1'>
                {chipText}
              </Chip>
            )}
            {healthStatus === HealthStatus.Inactive && (
              <Heading size={size === 'lg' ? 'h4' : 'h5'} color='secondary'>
                {t('loanHealth.loanHealthGauge.chipText.inactive')}
              </Heading>
            )}
          </FlexLayout>
        </div>
      </div>
    </FlexLayout>
  );
};

export { LoanHealthGauge };
